import type { DetailField } from '@/modules/infringements/components/detail/detail-header'
import {
  InfringementDetailHeader,
  WronglyGeneratedNotice,
} from '@/modules/infringements/components/detail/detail-header'
import {
  InfringementAttachmentsCard,
  InfringementFollowUpBlocks,
  InfringementStatusPills,
} from '@/modules/infringements/components/infringement-follow-up'
import { format } from '@/utils/date-fns'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ResultOf } from 'gql.tada'
import React from 'react'
import type { WORK_DAY_INFRINGEMENT_QUERY } from '../queries'
import { usePatchWorkInfringement } from './work-infringement-follow-up'

type WorkDayInfringementNode = NonNullable<
  ResultOf<typeof WORK_DAY_INFRINGEMENT_QUERY>['workDayInfringement']
>

interface Props {
  infringement: WorkDayInfringementNode
  displayIdentifier: string
  title: string
  description: string
  measuredValue: string
  secondaryValue: string | null
  periodText: string
}

/** The work day infringement's facts and follow-up, laid out like the
 * Absence page: the detail card, then its attachments. */
export const WorkDaySummary: React.FC<Props> = ({
  infringement,
  displayIdentifier,
  title,
  description,
  measuredValue,
  secondaryValue,
  periodText,
}) => {
  const translations = useTranslate({
    eyebrow: 'infringements.source.working-time',
    timestamp: 'common.timestamp',
    legalBasis: 'infringements.legal-basis',
    basis: 'common.basis',
    recordedValue: 'infringements.recorded-value',
    secondaryValue: 'infringements.secondary-value',
    period: 'common.period',
    archivedBasis: 'infringements.archived-basis',
    archivedDate: 'infringements.archived-date',
    wronglyGeneratedNotice: 'infringements.wrongly-generated-notice',
    wronglyGeneratedExplanation: 'infringements.wrongly-generated-explanation',
  })

  const { onPatch } = usePatchWorkInfringement(infringement.id)

  const fields: DetailField[] = [
    {
      label: translations.timestamp,
      value: format(
        new Date(infringement.infringementTimestamp),
        'dd.MM.yyyy HH:mm'
      ),
    },
    {
      label: translations.recordedValue,
      value: measuredValue,
      hint: secondaryValue
        ? `${translations.secondaryValue}: ${secondaryValue}`
        : undefined,
    },
  ]
  if (infringement.archivedBasis) {
    fields.push({
      label: translations.archivedBasis,
      value: infringement.archivedBasis,
    })
  }
  const info: DetailField[] = [
    { label: translations.basis, value: description },
    { label: translations.period, value: periodText },
    {
      label: translations.legalBasis,
      value: infringement.workInfringementCategory.legalBasis,
    },
  ]
  if (infringement.archivedAt) {
    info.push({
      label: translations.archivedDate,
      value: format(new Date(infringement.archivedAt), 'PP HH:mm'),
    })
  }

  return (
    <>
      {infringement.wronglyGenerated && (
        <WronglyGeneratedNotice
          title={translations.wronglyGeneratedNotice}
          explanationLabel={translations.wronglyGeneratedExplanation}
          explanation={infringement.wronglyGeneratedExplanation}
        />
      )}

      <InfringementDetailHeader
        eyebrow={translations.eyebrow}
        title={infringement.user.fullName}
        rule={`${displayIdentifier} · ${title}`}
        pills={<InfringementStatusPills infringement={infringement} />}
        fields={fields}
        info={info}
      >
        <InfringementFollowUpBlocks
          infringement={infringement}
          onPatch={onPatch}
        />
      </InfringementDetailHeader>

      <InfringementAttachmentsCard
        infringement={infringement}
        onPatch={onPatch}
      />
    </>
  )
}
